import { genericProductsLookup } from '@shared/generic-products';
import { ActivityId } from '@shared/models/activity-class.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { EnergyProductId } from '@shared/templates/categories/energy/energy.model';

/**
 * C-phase (end-of-life) IVL constants and material mappings.
 *
 * Implements the IVL "Anvisningar för LCA-beräkningar av byggprojekt" (2025-02)
 * sections S11 (C1), C2 transport, S12 (C3-C4 actual) and S13 (C3-C4 schablon).
 * See DEV-3321 and its subtasks DEV-3323 / DEV-3325 / DEV-3327.
 *
 * Only the BAU (business-as-usual) output is produced. The IVL climate-scenario
 * output (BAU × 0.63) is intentionally deferred to a separate ticket since it
 * requires a new parallel output dimension across the results model and UI.
 */

/** MJ → kWh conversion used to derive the per-MJ diesel GWP from the per-kWh value. */
const MJ_PER_KWH = 3.6;

/**
 * Operational energy GWP values (kg CO2e/kWh), read from the Nodon energy generic
 * products so C-phase stays aligned with B6 operational energy (DEV-3323).
 */
/** Read A1-A3 CO2e (kg/kWh) from a generic energy product without importing conversion helpers (avoids import cycles). */
const getEnergyProductGwpPerKwh = (productId: EnergyProductId): number =>
  genericProductsLookup[productId]?.conversion_factors?.['co2e_A1-A3'] ?? 0;

export const C_PHASE_GWP_ELECTRICITY_PER_KWH = getEnergyProductGwpPerKwh(
  EnergyProductId.Electricity,
);
export const C_PHASE_GWP_DIESEL_PER_KWH = getEnergyProductGwpPerKwh(
  EnergyProductId.DieselReductionObligation,
);
export const C_PHASE_GWP_DIESEL_PER_MJ =
  C_PHASE_GWP_DIESEL_PER_KWH / MJ_PER_KWH;

// --- C1: demolition (IVL S11) ---------------------------------------------

/** Material groups that drive the C1 demolition diesel add-ons (IVL S11). */
export type C1MaterialGroup = 'concrete' | 'masonry' | 'steel' | 'other';

/** Building-site electricity for demolition: setup 0.1 + demolition 8.0 (kWh/m² BTA). */
export const C1_ELECTRICITY_KWH_PER_M2 = 8.1;
/** Building-site base diesel for demolition: setup 1.0 + demolition 10.0 (kWh/m² BTA). */
export const C1_DIESEL_BASE_KWH_PER_M2 = 11.0;

/** Extra demolition diesel per ton of material mass (IVL S11), by material group. */
export const C1_MATERIAL_DIESEL_KWH_PER_TON: Record<C1MaterialGroup, number> = {
  concrete: 10.0,
  masonry: 5.0,
  steel: 1.1,
  other: 1.1,
};

/** Extra crane diesel per ton of material mass for tall buildings (IVL S11). */
export const C1_CRANE_DIESEL_KWH_PER_TON: Record<C1MaterialGroup, number> = {
  concrete: 4.1,
  masonry: 4.1,
  steel: 2.0,
  other: 2.0,
};

/**
 * Map a Nodon material category to its C1 group. Concrete and ceramics (masonry)
 * have distinct factors; steel and everything else share the same factors, so
 * any non-concrete/non-masonry material resolves to `'other'`.
 */
export const C1_MATERIAL_GROUP_BY_CATEGORY: Partial<
  Record<ElementCategoryID, C1MaterialGroup>
> = {
  [ElementCategoryID.Concrete]: 'concrete',
  [ElementCategoryID.Ceramics]: 'masonry',
  [ElementCategoryID.Metal]: 'steel',
};

// --- C2: transport of demolition waste -------------------------------------

/** Transport distance to waste processing (km), IVL schablon. */
export const C2_TRANSPORT_DISTANCE_KM = 50;
/** Truck energy use (MJ per ton-kilometre), IVL schablon. */
export const C2_TRUCK_ENERGY_MJ_PER_TONKM = 1.5;

// --- C3-C4: waste processing + disposal (IVL S12 / S13) --------------------

/** S12 material groups for actual-resource C3-C4 (kg CO2e/kg). */
export type S12MaterialGroup =
  | 'reinforcement'
  | 'binderMortarAggregate'
  | 'buildingBlock'
  | 'woodBoards'
  | 'readyMixConcrete'
  | 'gypsumBoards'
  | 'glassOther'
  | 'insulationGlassWool'
  | 'insulationCellPlastic'
  | 'insulationStoneWool'
  | 'plasticOther'
  | 'precastConcrete'
  | 'steelSheetMetal'
  | 'woodProducts'
  | 'solar'
  | 'other';

/** S12 climate data for C3-C4 (kg CO2e/kg), keyed by material group (IVL table S12). */
export const S12_FACTOR_BY_GROUP: Record<S12MaterialGroup, number> = {
  reinforcement: 0.002,
  binderMortarAggregate: 0.005,
  buildingBlock: 0.005,
  woodBoards: 0.02,
  readyMixConcrete: 0.006,
  gypsumBoards: 0.00077,
  glassOther: 0.02,
  insulationGlassWool: 0.005,
  insulationCellPlastic: 0.02,
  insulationStoneWool: 0.005,
  plasticOther: 0.02,
  precastConcrete: 0.006,
  steelSheetMetal: 0.002,
  woodProducts: 0.02,
  solar: 0.5,
  other: 0.02,
};

/** Default S12 factor (kg CO2e/kg) for products without an explicit material group ("Övrigt"). */
export const S12_DEFAULT_FACTOR = S12_FACTOR_BY_GROUP.other;

/**
 * Map a Nodon product {@link ElementCategoryID} to its S12 material group, so actual
 * resources get a representative C3-C4 factor from their category even when the product
 * carries no explicit `characteristics.c3c4` annotation (DEV-3327). A product's own `c3c4`
 * still wins; this is the category-level fallback before the "Övrigt" default.
 *
 * Categories that map to several S12 rows use the dominant material by mass: insulation →
 * mineral (stone) wool, windows/doors → glass. Concrete uses the ready-mix/precast factor
 * (both 0.006). Categories without a clear material (Labour, Energy, OtherProduct) are left
 * unmapped and fall through to the default.
 */
export const S12_GROUP_BY_CATEGORY: Partial<
  Record<ElementCategoryID, S12MaterialGroup>
> = {
  [ElementCategoryID.Concrete]: 'readyMixConcrete',
  [ElementCategoryID.Wood]: 'woodProducts',
  [ElementCategoryID.Metal]: 'steelSheetMetal',
  [ElementCategoryID.Gypsum]: 'gypsumBoards',
  [ElementCategoryID.Ceramics]: 'buildingBlock',
  [ElementCategoryID.Insulation]: 'insulationStoneWool',
  [ElementCategoryID.WindowsDoors]: 'glassOther',
};

/** S13 building types used for the BD7/BD8 schablon rows (IVL table S13). */
export type S13BuildingType =
  | 'apartmentBlock'
  | 'office'
  | 'school'
  | 'preschool'
  | 'house'
  | 'specialHousing';

export type S13Schablon = {
  /** BD7 (interior surface layers) mass per m² Atemp (kg/m²) */
  bd7KgPerM2: number;
  /** BD8 (installations) mass per m² Atemp (kg/m²) */
  bd8KgPerM2: number;
  /** BD7 emission factor (kg CO2e/kg) */
  bd7Factor: number;
  /** BD8 emission factor (kg CO2e/kg) */
  bd8Factor: number;
};

/** S13 BD7/BD8 schablon values per building type (IVL table S13). */
export const S13_SCHABLON_BY_BUILDING_TYPE: Record<
  S13BuildingType,
  S13Schablon
> = {
  apartmentBlock: {
    bd7KgPerM2: 44,
    bd8KgPerM2: 9,
    bd7Factor: 0.013,
    bd8Factor: 0.007,
  },
  office: {
    bd7KgPerM2: 17,
    bd8KgPerM2: 23,
    bd7Factor: 0.014,
    bd8Factor: 0.007,
  },
  school: {
    bd7KgPerM2: 18,
    bd8KgPerM2: 24,
    bd7Factor: 0.013,
    bd8Factor: 0.007,
  },
  preschool: {
    bd7KgPerM2: 18,
    bd8KgPerM2: 24,
    bd7Factor: 0.013,
    bd8Factor: 0.007,
  },
  house: { bd7KgPerM2: 44, bd8KgPerM2: 9, bd7Factor: 0.013, bd8Factor: 0.007 },
  specialHousing: {
    bd7KgPerM2: 44,
    bd8KgPerM2: 9,
    bd7Factor: 0.013,
    bd8Factor: 0.007,
  },
};

/**
 * Map a Nodon {@link ActivityId} to its S13 building type. Residential variants all
 * share the same schablon values, so anything not explicitly office/school maps to the
 * residential `apartmentBlock` row (also the default for unset activities).
 */
export const S13_BUILDING_TYPE_BY_ACTIVITY: Partial<
  Record<ActivityId, S13BuildingType>
> = {
  [ActivityId.Office]: 'office',
  [ActivityId.IndustryOffice]: 'office',
  [ActivityId.School]: 'school',
  [ActivityId.PrivateHousing]: 'house',
  [ActivityId.CollectiveHousing]: 'apartmentBlock',
  [ActivityId.SpecialNeedHousing]: 'specialHousing',
};

/** Building type used when a project has no recognised activity. */
export const S13_DEFAULT_BUILDING_TYPE: S13BuildingType = 'apartmentBlock';
